// frontend/src/pages/ProfilePage.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../AuthContext";
import { useFavorites } from "../context/FavoritesContext";

import {
  getSmartRecommendations,
  searchGames,
  getGameRecommendations,
  getSimilarGames,
  getPopularGames,
} from "../api/games";
import { generateSmartList } from "../api/smartLists";
import {
  getUserLists,
  createCustomList,
  deleteCustomList,
  addGameToList,
  removeGameFromList,
} from "../api/customLists";
import { recordInteraction, INTERACTION_TYPES } from "../api/interactions";
import GameCard from "../components/games/GameCard";
import PasswordDialog from "../components/PasswordDialog";
import ProfileDialog from "../components/ProfileDialog";
import httprequest from "./../axios";

import GlassPanel from "../components/ui/GlassPanel";
import Button from "../components/ui/Button";
import Modal from "../components/ui/Modal";
import TextInput from "../components/ui/TextInput";
import TagMultiSelect from "../components/ui/TagMultiSelect";
import InfoTooltip from "../components/ui/InfoTooltip";
import Tooltip from "../components/ui/Tooltip";

// Custom hook to preserve input focus and prevent scroll issues
const useStableInput = (initialValue = "") => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);
  const focusPreservedRef = useRef(false);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    focusPreservedRef.current = true;
  };

  // Prevent focus loss and scroll jumping
  useEffect(() => {
    if (focusPreservedRef.current && inputRef.current) {
      const cursorPosition = inputRef.current.selectionStart;
      requestAnimationFrame(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
      focusPreservedRef.current = false;
    }
  });

  return { value, handleChange, inputRef, setValue };
};

const TABS = [
  { label: "Overview", icon: "🎮", desc: "Your account settings & profile" },
  { label: "My Lists", icon: "📋", desc: "Create and manage your game lists" },
  { label: "Statistics", icon: "📊", desc: "Insights from your favourites" },
  { label: "For You", icon: "✨", desc: "Personalised game recommendations" },
  { label: "Blacklist", icon: "🚫", desc: "Games you've hidden from results" },
];

// Hoisted to module scope so it keeps a stable identity across renders
// (defining components inside the page is what caused remount "jerk").
const StatCard = ({ icon, value, label, accent = "", delay = 0 }) => (
  <div
    style={{ animationDelay: `${delay}ms` }}
    className={[
      "group rounded-2xl border p-5 flex items-center gap-4 backdrop-blur-md animate-fadeUp transition-all duration-300 hover:-translate-y-1",
      accent || "border-white/10 bg-white/[0.04]",
    ].join(" ")}
  >
    <span className="flex items-center justify-center h-14 w-14 rounded-2xl bg-white/[0.07] text-3xl shrink-0">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="font-heading font-extrabold text-3xl text-white leading-none">{value}</p>
      <p className="text-white/60 text-sm mt-1.5 truncate">{label}</p>
    </div>
  </div>
);

const ProfilePage = () => {
  const { user, updateUserProfile, blacklist } = useAuth();
  const { favoriteGames } = useFavorites(); // Use favoriteGames for full game data

  // Profile state
  const [activeTab, setActiveTab] = useState(0);

  // Custom lists state - now from database instead of localStorage
  const [customLists, setCustomLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [isCreateListDialogOpen, setIsCreateListDialogOpen] = useState(false);
  const [selectedListForGames, setSelectedListForGames] = useState(null);
  const [isGameSelectionDialogOpen, setIsGameSelectionDialogOpen] =
    useState(false);
  const [availableGames, setAvailableGames] = useState([]);
  const [searchingGames, setSearchingGames] = useState(false);

  // Smart list generator state
  const [isSmartListDialogOpen, setIsSmartListDialogOpen] = useState(false);
  const [smartListCriteria, setSmartListCriteria] = useState({
    genres: [],
    platforms: [],
    tags: [],
    themes: "",
    gameMode: "",
    rating: 4,
    limit: 10,
    listName: "",
  });
  const [generatingSmartList, setGeneratingSmartList] = useState(false);
  const [generatedList, setGeneratedList] = useState(null);
  const [showGeneratedListDialog, setShowGeneratedListDialog] = useState(false);
  const [savingGeneratedList, setSavingGeneratedList] = useState(false);

  // Track which list is currently being deleted (so we can show a spinner)
  const [deletingListId, setDeletingListId] = useState(null);

  // View a saved list (open it to see all its games)
  const [viewList, setViewList] = useState(null);
  const [isViewListDialogOpen, setIsViewListDialogOpen] = useState(false);

  // Dropdown options for smart list generation
  const genreOptions = [
    "Action",
    "Adventure",
    "RPG",
    "Strategy",
    "Simulation",
    "Sports",
    "Racing",
    "Puzzle",
    "Platformer",
    "Fighting",
    "Shooter",
    "Horror",
    "Indie",
    "Casual",
    "Family Friendly",
    "Educational",
    "Board Games",
    "Card",
  ];

  const platformOptions = [
    "PC",
    "PlayStation 5",
    "PlayStation 4",
    "Xbox Series S/X",
    "Xbox One",
    "Nintendo Switch",
    "iOS",
    "Android",
    "macOS",
    "Linux",
  ];

  const tagOptions = [
    "Singleplayer",
    "Multiplayer",
    "Co-op",
    "Open World",
    "Story Rich",
    "Atmospheric",
    "Great Soundtrack",
    "Fantasy",
    "Sci-fi",
    "Third Person",
    "First-Person",
    "Dark",
    "Comedy",
    "Horror",
    "Mystery",
    "Romance",
    "Survival",
    "Crafting",
    "Building",
    "Management",
    "Exploration",
    "Combat",
    "Stealth",
    "Magic",
    "Dragons",
    "Zombies",
    "Pirates",
    "Space",
    "Cyberpunk",
    "Post-apocalyptic",
  ];

  // Use stable inputs for dialog fields to prevent focus loss
  const listNameInput = useStableInput("");
  const gameSearchInput = useStableInput("");

  const blacklistRefresh = () => {
    getBlackList();
  };

  const [blacklistgames, setBlacklistgames] = useState([]);
  const getBlackList = async () => {
    try {
      const response = await httprequest.get("/games/blocked-games");
      setBlacklistgames(response.data.blockedGames);
    } catch (error) {
      console.error("Error getting games", error);
      return [];
    }
  };

  // Load custom lists from database on component mount
  useEffect(() => {
    getBlackList();
    loadCustomLists();
  }, []);

  const loadCustomLists = async () => {
    setLoadingLists(true);
    try {
      const lists = await getUserLists();

      setCustomLists(lists);
    } catch (error) {
      console.error("Error loading custom lists from database:", error);
    } finally {
      setLoadingLists(false);
    }
  };

  // Statistics state - removed games played, now dynamic
  const userStats = useMemo(
    () => ({
      totalFavorites: favoriteGames?.length || 0,
      totalLists: customLists?.length || 0,
      averageRating: 4.2,
    }),
    [favoriteGames, customLists],
  );

  // Personalized recommendations state - based on favorites
  const [personalizedRecommendations, setPersonalizedRecommendations] =
    useState([]);
  const [recLoading, setRecLoading] = useState(false);

  // Load recommendations based on favorites — request exactly 4 recommendations total
  useEffect(() => {
    const loadRecommendations = async () => {
      if (!favoriteGames || favoriteGames.length === 0) {
        try {
          const popularResp = await getPopularGames(1, 4, blacklist || []);
          const popular = popularResp?.games || [];
          setPersonalizedRecommendations(popular.slice(0, 4));
        } catch (e) {
          console.error("❌ Failed to load popular games fallback", e);
          setPersonalizedRecommendations([]);
        }
        setRecLoading(false);
        return;
      }

      setRecLoading(true);
      try {
        // Pick the top favorite (first valid) as the seed and request 4 recommendations
        const validFavorites = favoriteGames.filter(
          (g) => g && g.id && g.id !== "undefined" && g.id !== "null",
        );
        if (validFavorites.length === 0) {
          setPersonalizedRecommendations([]);
          setRecLoading(false);
          return;
        }

        const seed = validFavorites[0];
        const recs = await getSmartRecommendations(seed.id, 4);

        // Always use recs.games (array) for filtering, fallback to [] if missing
        let unique = (Array.isArray(recs?.games) ? recs.games : [])
          .filter(
            (g, idx, arr) =>
              g && g.id && idx === arr.findIndex((x) => x && x.id === g.id),
          )
          .slice(0, 4);

        // If fewer than 4, try additional fallbacks to fill slots
        const needed = () => Math.max(0, 4 - unique.length);
        if (unique.length < 4) {
          try {
            const need = needed();
            if (need > 0) {
              const more = await getGameRecommendations(seed.id, need);
              unique = unique
                .concat(more || [])
                .filter(
                  (g, idx, arr) =>
                    g &&
                    g.id &&
                    idx === arr.findIndex((x) => x && x.id === g.id),
                )
                .slice(0, 4);
            }
          } catch (e) {
            console.warn("Fallback getGameRecommendations failed", e);
          }
        }

        if (unique.length < 4) {
          try {
            const need = needed();
            if (need > 0) {
              const more = await getSimilarGames(seed.id, need);
              unique = unique
                .concat(more || [])
                .filter(
                  (g, idx, arr) =>
                    g &&
                    g.id &&
                    idx === arr.findIndex((x) => x && x.id === g.id),
                )
                .slice(0, 4);
            }
          } catch (e) {
            console.warn("Fallback getSimilarGames failed", e);
          }
        }

        if (unique.length < 4) {
          try {
            const need = needed();
            if (need > 0) {
              const moreResp = await getPopularGames(1, need, blacklist || []);
              const more = moreResp?.games || [];
              unique = unique
                .concat(more || [])
                .filter(
                  (g, idx, arr) =>
                    g &&
                    g.id &&
                    idx === arr.findIndex((x) => x && x.id === g.id),
                )
                .slice(0, 4);
            }
          } catch (e) {
            console.warn("Fallback getPopularGames failed", e);
          }
        }

        setPersonalizedRecommendations(unique);
      } catch (error) {
        console.error("❌ Error loading personalized recommendations:", error);
        setPersonalizedRecommendations([]);
      } finally {
        setRecLoading(false);
      }
    };

    loadRecommendations();
  }, [favoriteGames, blacklist]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);

  // Auto-hide the toast message after 5s, same as the old Snackbar's autoHideDuration
  useEffect(() => {
    if (!message.text) return;
    const t = setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    return () => clearTimeout(t);
  }, [message.text]);

  // Form handlers
  const handleProfileUpdate = async (profileData) => {
    setLoading(true);
    try {
      await updateUserProfile(profileData);
      setMessage({ type: "success", text: "Profile updated successfully!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update profile" });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (passwordData) => {
    setLoading(true);
    try {
      // Use the existing updateUserProfile function to change password
      await updateUserProfile({ password: passwordData.newPassword });
      setMessage({ type: "success", text: "Password changed successfully!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to change password" });
      throw error; // Re-throw so dialog can handle it
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = async () => {
    if (listNameInput.value.trim()) {
      try {
        const newList = await createCustomList(listNameInput.value.trim());
        setCustomLists((prev) => [newList, ...prev]);
        listNameInput.setValue("");
        setIsCreateListDialogOpen(false);
        setMessage({ type: "success", text: "List created successfully!" });

        // Record interaction for list creation
        await recordInteraction(null, INTERACTION_TYPES.CUSTOM_LIST_CREATE, {
          listName: listNameInput.value.trim(),
          type: "custom_list_creation",
        });
      } catch (error) {
        console.error("Error creating list:", error);
        setMessage({
          type: "error",
          text: "Failed to create list. Please try again.",
        });
      }
    }
  };

  const handleDeleteList = async (listId) => {
    // Guard against double-clicks while a delete is in flight
    if (deletingListId) return;
    setDeletingListId(listId);
    try {
      await deleteCustomList(listId);
      setCustomLists((prev) => prev.filter((list) => list.id !== listId));
      // Close the view dialog if the deleted list was open
      if (viewList && viewList.id === listId) {
        setIsViewListDialogOpen(false);
        setViewList(null);
      }
      setMessage({ type: "success", text: "List deleted successfully!" });
    } catch (error) {
      console.error("Error deleting list:", error);
      setMessage({
        type: "error",
        text: "Failed to delete list. Please try again.",
      });
    } finally {
      setDeletingListId(null);
    }
  };

  // Open a saved list to view all of its games
  const handleOpenListView = (list) => {
    setViewList(list);
    setIsViewListDialogOpen(true);
  };

  // Smart list generator handlers
  const handleGenerateSmartList = async () => {
    // Validate that at least some criteria are provided
    const hasGenres =
      smartListCriteria.genres && smartListCriteria.genres.length > 0;
    const hasPlatforms =
      smartListCriteria.platforms && smartListCriteria.platforms.length > 0;
    const hasTags = smartListCriteria.tags && smartListCriteria.tags.length > 0;
    const hasThemes =
      smartListCriteria.themes && smartListCriteria.themes.trim().length > 0;
    const hasGameMode =
      smartListCriteria.gameMode &&
      smartListCriteria.gameMode.trim().length > 0;

    if (!hasGenres && !hasPlatforms && !hasTags && !hasThemes && !hasGameMode) {
      setMessage({
        type: "warning",
        text: "Please specify at least one criteria (genres, platforms, tags, themes, or game mode) to generate a smart list.",
      });
      return;
    }

    setGeneratingSmartList(true);
    try {
      const result = await generateSmartList(smartListCriteria);
      if (result && result.list) {
        setGeneratedList(result.list);
        setShowGeneratedListDialog(true);
        setIsSmartListDialogOpen(false);
      }
    } catch (error) {
      console.error("Error generating smart list:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate smart list";
      setMessage({ type: "error", text: `Error: ${errorMessage}` });
    } finally {
      setGeneratingSmartList(false);
    }
  };

  const handleSaveGeneratedList = async () => {
    // Guard against duplicate saves: ignore if there's nothing to save or a
    // save is already in progress (prevents rapid multi-click duplicates).
    if (!generatedList || savingGeneratedList) return;

    // Validate that the generated list actually has content to save
    const games = Array.isArray(generatedList.games) ? generatedList.games : [];
    if (!generatedList.name || games.length === 0) {
      setMessage({
        type: "warning",
        text: "Nothing to save — the generated list is empty.",
      });
      return;
    }

    setSavingGeneratedList(true);
    try {
      // Create the list in database
      const newList = await createCustomList(
        generatedList.name,
        generatedList.description || "",
        true,
        generatedList.criteria,
      );

      // Add all games to the list in parallel (much faster than sequential,
      // which was the cause of the ~1 minute delay). Failures are tolerated
      // per-game so one bad game doesn't fail the whole save.
      const results = await Promise.allSettled(
        games
          .filter((game) => game && game.id)
          .map((game) => addGameToList(newList.id, game.id)),
      );
      results.forEach((r, i) => {
        if (r.status === "rejected") {
          console.error(`Failed to add game ${games[i]?.id} to list:`, r.reason);
        }
      });

      // Reload lists to get updated version with games
      await loadCustomLists();

      setShowGeneratedListDialog(false);
      setGeneratedList(null);
      setMessage({
        type: "success",
        text: "Smart list saved! Find it under the “My Lists” tab.",
      });

      // Reset criteria
      setSmartListCriteria({
        genres: [],
        platforms: [],
        tags: [],
        themes: "",
        mood: "",
        gameMode: "",
        rating: 7,
        limit: 10,
        listName: "",
      });
    } catch (error) {
      console.error("Error saving generated list:", error);
      setMessage({ type: "error", text: "Failed to save generated list" });
    } finally {
      setSavingGeneratedList(false);
    }
  };

  const handleShareList = (list) => {
    if (navigator.share) {
      navigator.share({
        title: `${list.name} - Game List`,
        text: `Check out this curated game list: ${list.name}`,
        url: window.location.href,
      });
    } else {
      // Fallback - copy to clipboard
      const shareText = `${list.name}\n\nGames:\n${list.games.map((g) => `• ${g.title || g.name}`).join("\n")}\n\nFrom Game Goblin`;
      navigator.clipboard.writeText(shareText);
      setMessage({ type: "success", text: "List copied to clipboard!" });
    }
  };

  // Helper functions for statistics calculation
  const getMostCommonPlatform = () => {
    if (!favoriteGames || favoriteGames.length === 0) return "PlayStation 4";

    const platformCounts = {};
    favoriteGames.forEach((game) => {
      let platforms = game.platforms || game.platform || [];

      if (typeof platforms === "string") {
        try {
          platforms = JSON.parse(platforms);
        } catch (e) {
          platforms = [platforms];
        }
      }

      if (!Array.isArray(platforms)) {
        platforms = platforms ? [platforms] : [];
      }

      platforms.forEach((platform) => {
        const platformName =
          typeof platform === "object"
            ? platform.name || platform.platform
            : platform;
        if (platformName && typeof platformName === "string") {
          platformCounts[platformName] =
            (platformCounts[platformName] || 0) + 1;
        }
      });
    });

    const entries = Object.entries(platformCounts);
    if (entries.length === 0) return "PlayStation 4";

    const mostCommon = entries.reduce((a, b) =>
      platformCounts[a[0]] > platformCounts[b[0]] ? a : b,
    )[0];

    return mostCommon;
  };

  const getMostCommonPlatformCount = () => {
    if (!favoriteGames || favoriteGames.length === 0) return 2;
    const platformCounts = {};
    favoriteGames.forEach((game) => {
      let platforms = game.platforms || game.platform || [];

      if (typeof platforms === "string") {
        try {
          platforms = JSON.parse(platforms);
        } catch (e) {
          platforms = [platforms];
        }
      }

      if (!Array.isArray(platforms)) {
        platforms = platforms ? [platforms] : [];
      }

      platforms.forEach((platform) => {
        const platformName =
          typeof platform === "object"
            ? platform.name || platform.platform
            : platform;
        if (platformName && typeof platformName === "string") {
          platformCounts[platformName] =
            (platformCounts[platformName] || 0) + 1;
        }
      });
    });

    const maxCount = Math.max(...Object.values(platformCounts), 0);
    return maxCount;
  };

  const getMostCommonGenre = () => {
    if (!favoriteGames || favoriteGames.length === 0) return "Action RPG";

    const genreCounts = {};
    favoriteGames.forEach((game) => {
      let genres = game.genres || game.genre || [];

      if (typeof genres === "string") {
        try {
          genres = JSON.parse(genres);
        } catch (e) {
          genres = [genres];
        }
      }

      if (!Array.isArray(genres)) {
        genres = genres ? [genres] : [];
      }

      genres.forEach((genre) => {
        const genreName =
          typeof genre === "object" ? genre.name || genre.genre : genre;
        if (genreName && typeof genreName === "string") {
          genreCounts[genreName] = (genreCounts[genreName] || 0) + 1;
        }
      });
    });

    const entries = Object.entries(genreCounts);
    if (entries.length === 0) return "Action RPG";

    const mostCommon = entries.reduce((a, b) =>
      genreCounts[a[0]] > genreCounts[b[0]] ? a : b,
    )[0];

    return mostCommon;
  };

  const getMostCommonGenreCount = () => {
    if (!favoriteGames || favoriteGames.length === 0) return 2;

    const genreCounts = {};
    favoriteGames.forEach((game) => {
      let genres = game.genres || game.genre || [];

      if (typeof genres === "string") {
        try {
          genres = JSON.parse(genres);
        } catch (e) {
          genres = [genres];
        }
      }

      if (!Array.isArray(genres)) {
        genres = genres ? [genres] : [];
      }

      genres.forEach((genre) => {
        const genreName =
          typeof genre === "object" ? genre.name || genre.genre : genre;
        if (genreName && typeof genreName === "string") {
          genreCounts[genreName] = (genreCounts[genreName] || 0) + 1;
        }
      });
    });

    const maxCount = Math.max(...Object.values(genreCounts), 0);
    return maxCount;
  };

  const getTopTags = () => {
    if (!favoriteGames || favoriteGames.length === 0) {
      return [
        { name: "Open World", count: 3, percentage: 30 },
        { name: "Story Rich", count: 2, percentage: 25 },
        { name: "Single Player", count: 2, percentage: 20 },
      ];
    }

    const tagCounts = {};
    favoriteGames.forEach((game) => {
      // Check multiple possible tag fields
      const tagSources = [
        game.tags,
        game.themes,
        game.keywords,
        game.game_tags,
        game.gameTags,
        game.game_modes,
      ];

      tagSources.forEach((tags) => {
        if (!tags) return;

        // Handle string parsing
        let processedTags = tags;
        if (typeof tags === "string") {
          try {
            processedTags = JSON.parse(tags);
          } catch (e) {
            processedTags = [tags];
          }
        }

        if (!Array.isArray(processedTags)) {
          processedTags = processedTags ? [processedTags] : [];
        }

        processedTags.forEach((tag) => {
          const tagName =
            typeof tag === "object"
              ? tag.name || tag.tag || tag.theme || tag.keyword
              : tag;
          if (tagName && typeof tagName === "string") {
            tagCounts[tagName] = (tagCounts[tagName] || 0) + 1;
          }
        });
      });
    });

    const entries = Object.entries(tagCounts);
    if (entries.length === 0) {
      // Fallback: extract tags from genres if no dedicated tags exist
      const genreTags = {};
      favoriteGames.forEach((game) => {
        let genres = game.genres || game.genre || [];

        if (typeof genres === "string") {
          try {
            genres = JSON.parse(genres);
          } catch (e) {
            genres = [genres];
          }
        }

        if (!Array.isArray(genres)) {
          genres = genres ? [genres] : [];
        }

        genres.forEach((genre) => {
          const genreName =
            typeof genre === "object" ? genre.name || genre.genre : genre;
          if (genreName && typeof genreName === "string") {
            genreTags[genreName] = (genreTags[genreName] || 0) + 1;
          }
        });
      });

      return Object.entries(genreTags)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({
          name,
          count,
          percentage: Math.round((count / favoriteGames.length) * 100),
        }));
    }

    const topTags = entries
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({
        name,
        count,
        percentage: Math.round((count / favoriteGames.length) * 100),
      }));

    return topTags;
  };

  const getUniqueGamesCount = () => {
    if (!customLists || customLists.length === 0) return 0;

    const uniqueGameIds = new Set();
    customLists.forEach((list) => {
      if (Array.isArray(list.games)) {
        list.games.forEach((game) => {
          uniqueGameIds.add(game.id);
        });
      }
    });

    return uniqueGameIds.size;
  };

  const handleOpenGameSelection = async (list) => {
    setSelectedListForGames(list);
    setIsGameSelectionDialogOpen(true);
    await loadAvailableGames();
  };

  const loadAvailableGames = async () => {
    setSearchingGames(true);
    try {
      const response = await searchGames(
        { query: gameSearchInput.value },
        1,
        20,
        blacklist || [],
      );
      setAvailableGames(response.games || []);
    } catch (error) {
      console.error("Error loading available games:", error);
      setAvailableGames([]);
    } finally {
      setSearchingGames(false);
    }
  };

  const handleAddGameToList = async (game) => {
    if (!selectedListForGames) return;

    try {
      await addGameToList(selectedListForGames.id, game.id);

      // Update local state
      setCustomLists((prev) =>
        prev.map((list) => {
          if (list.id === selectedListForGames.id) {
            const gameExists = list.games.some((g) => g.id === game.id);
            if (!gameExists) {
              const updatedList = {
                ...list,
                games: [
                  ...list.games,
                  { ...game, addedAt: new Date().toISOString() },
                ],
                count: list.games.length + 1,
              };
              // Update the selected list state so the UI reflects the change immediately
              setSelectedListForGames(updatedList);
              return updatedList;
            }
          }
          return list;
        }),
      );

      setMessage({
        type: "success",
        text: `${game.title || game.name} added to list!`,
      });

      // Record interaction
      await recordInteraction(game.id, INTERACTION_TYPES.CUSTOM_LIST_ADD, {
        listName: selectedListForGames.name,
        listId: selectedListForGames.id,
        type: "custom_list_add",
      });
    } catch (error) {
      console.error("Error adding game to list:", error);
      setMessage({ type: "error", text: "Failed to add game to list" });
    }
  };

  const handleRemoveGameFromList = async (listId, gameId, gameTitle) => {
    try {
      await removeGameFromList(listId, gameId);

      // Update local state
      setCustomLists((prev) =>
        prev.map((list) => {
          if (list.id === listId) {
            return {
              ...list,
              games: list.games.filter((g) => g.id !== gameId),
              count: list.games.length - 1,
            };
          }
          return list;
        }),
      );

      if (selectedListForGames && selectedListForGames.id === listId) {
        setSelectedListForGames((prev) => ({
          ...prev,
          games: prev.games.filter((g) => g.id !== gameId),
          count: prev.games.length - 1,
        }));
      }

      setMessage({ type: "success", text: `${gameTitle} removed from list` });
    } catch (error) {
      console.error("Error removing game from list:", error);
      setMessage({ type: "error", text: "Failed to remove game from list" });
    }
  };

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
  };

  const sectionHeading = (title) => (
    <h2 className="flex items-center gap-3 mb-6 font-heading font-extrabold text-2xl sm:text-3xl text-white">
      <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
      {title}
    </h2>
  );

  // ---------------------------------------------------------------------
  // Tab content — rendered as inline functions (NOT nested components) so
  // React never remounts them on state changes. This removes the focus loss
  // and layout "jerk" when typing or adding games/lists.
  // ---------------------------------------------------------------------

  const renderProfileTab = () => (
    <div className="max-w-3xl mx-auto">
      {sectionHeading("Account settings")}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <GlassPanel className="p-6 flex flex-col animate-fadeUp">
          <span className="flex items-center justify-center h-12 w-12 rounded-2xl bg-brand-purple/15 border border-brand-purple/30 text-2xl mb-4">
            👤
          </span>
          <h3 className="font-heading font-bold text-lg text-white mb-1">
            Personal information
          </h3>
          <p className="text-white/55 text-sm mb-5 flex-1">
            Update your username and view your account details.
          </p>
          <Tooltip label="Open a dialog to edit your username" wide>
            <Button variant="primary" onClick={() => setIsProfileDialogOpen(true)}>
              Update profile
            </Button>
          </Tooltip>
        </GlassPanel>

        <GlassPanel className="p-6 flex flex-col animate-fadeUp" >
          <span className="flex items-center justify-center h-12 w-12 rounded-2xl bg-brand-gold/15 border border-brand-gold/30 text-2xl mb-4">
            🔒
          </span>
          <h3 className="font-heading font-bold text-lg text-white mb-1">
            Change password
          </h3>
          <p className="text-white/55 text-sm mb-5 flex-1">
            Update your password to keep your account secure.
          </p>
          <Tooltip label="Open a dialog to set a new password" wide>
            <Button variant="gold" onClick={() => setIsPasswordDialogOpen(true)}>
              Change password
            </Button>
          </Tooltip>
        </GlassPanel>
      </div>

      <PasswordDialog
        open={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
        onPasswordChange={handlePasswordChange}
        loading={loading}
      />

      <ProfileDialog
        open={isProfileDialogOpen}
        onClose={() => setIsProfileDialogOpen(false)}
        onProfileUpdate={handleProfileUpdate}
        loading={loading}
      />
    </div>
  );

  const renderListsTab = () => (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h2 className="flex items-center gap-3 font-heading font-extrabold text-2xl sm:text-3xl text-white">
          <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
          My Lists
          <span className="text-base font-bold text-brand-gold-soft bg-brand-gold/10 border border-brand-gold/30 rounded-full px-3 py-0.5">
            {customLists.length}
          </span>
        </h2>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <Tooltip label="Auto-build a list from your chosen genres, platforms & tags" side="bottom" wide>
            <Button variant="outline" onClick={() => setIsSmartListDialogOpen(true)}>
              ✨ Auto-Generate
            </Button>
          </Tooltip>
          <Tooltip label="Create a new empty list to organize games" side="bottom" wide>
            <Button variant="primary" onClick={() => setIsCreateListDialogOpen(true)}>
              + Create List
            </Button>
          </Tooltip>
        </div>
      </div>

      {loadingLists ? (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 rounded-full border-4 border-brand-purple/30 border-t-brand-purple animate-spin" />
        </div>
      ) : customLists.length === 0 ? (
        <GlassPanel className="p-12 text-center animate-fadeUp">
          <div className="text-6xl mb-4 opacity-70">📋</div>
          <h3 className="mb-2 font-heading font-bold text-2xl text-white">
            No lists yet
          </h3>
          <p className="mb-5 text-white/55">
            Create your first list to organize the games you love.
          </p>
          <Button variant="primary" onClick={() => setIsCreateListDialogOpen(true)}>
            + Create your first list
          </Button>
        </GlassPanel>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
          {customLists.map((list, idx) => (
            <GlassPanel
              key={list.id}
              style={{ animationDelay: `${idx * 45}ms` }}
              className="p-5 flex flex-col animate-fadeUp hover:border-brand-gold/50 hover:-translate-y-1 hover:shadow-card-hover transition-all duration-300"
            >
              <Tooltip label="Open this list to view all its games" side="bottom" wide>
                <button
                  type="button"
                  onClick={() => handleOpenListView(list)}
                  className="flex items-start gap-3 mb-3 w-full text-left group/open"
                >
                  <span className="flex items-center justify-center h-11 w-11 rounded-xl bg-brand-purple/15 border border-brand-purple/30 text-xl shrink-0">
                    📋
                  </span>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading font-bold text-lg text-white truncate group-hover/open:text-brand-gold transition-colors">
                      {list.name}
                    </h4>
                    <p className="text-white/45 text-sm">{list.games?.length || 0} games</p>
                  </div>
                </button>
              </Tooltip>

              {list.games && list.games.length > 0 && (
                <div className="mb-3">
                  <div className="flex flex-wrap gap-1.5">
                    {list.games.slice(0, 3).map((game) => (
                      <span
                        key={game.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.05] border border-white/10 text-white/85 text-xs px-2.5 py-1"
                      >
                        {game.title || game.name}
                        <Tooltip label={`Remove "${game.title || game.name}" from this list`} wide>
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveGameFromList(
                                list.id,
                                game.id,
                                game.title || game.name,
                              )
                            }
                            className="text-white/40 hover:text-red-400 transition-colors"
                            aria-label="Remove game from list"
                          >
                            ✕
                          </button>
                        </Tooltip>
                      </span>
                    ))}
                    {list.games.length > 3 && (
                      <span className="inline-flex items-center rounded-full bg-brand-gold/15 border border-brand-gold/30 text-brand-gold text-xs px-2.5 py-1">
                        +{list.games.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-auto pt-3 flex items-center justify-between flex-wrap gap-2 border-t border-white/10">
                <div className="flex gap-1 flex-wrap">
                  <Tooltip label="Open this list to view all its games" side="bottom" wide>
                    <button
                      type="button"
                      onClick={() => handleOpenListView(list)}
                      className="text-white font-semibold text-sm hover:bg-white/10 rounded-lg px-2 py-1 transition-colors"
                    >
                      👁 Open
                    </button>
                  </Tooltip>
                  <Tooltip label="Search and add games to this list" side="bottom" wide>
                    <button
                      type="button"
                      onClick={() => handleOpenGameSelection(list)}
                      className="text-brand-purple-light font-semibold text-sm hover:bg-brand-purple/10 rounded-lg px-2 py-1 transition-colors"
                    >
                      + Add games
                    </button>
                  </Tooltip>
                  <Tooltip label="Share this list (or copy it to your clipboard)" side="bottom" wide>
                    <button
                      type="button"
                      onClick={() => handleShareList(list)}
                      className="text-brand-gold font-semibold text-sm hover:bg-brand-gold/10 rounded-lg px-2 py-1 transition-colors"
                    >
                      ↗ Share
                    </button>
                  </Tooltip>
                </div>
                <Tooltip label="Permanently delete this list" side="bottom" wide>
                  <button
                    type="button"
                    onClick={() => handleDeleteList(list.id)}
                    disabled={deletingListId === list.id}
                    className={[
                      "font-semibold text-sm rounded-lg px-2 py-1 transition-colors inline-flex items-center gap-1.5",
                      deletingListId === list.id
                        ? "text-red-300 cursor-not-allowed"
                        : "text-white/45 hover:text-red-400 hover:bg-red-400/10",
                    ].join(" ")}
                  >
                    {deletingListId === list.id ? (
                      <>
                        <span className="h-3.5 w-3.5 rounded-full border-2 border-red-300/40 border-t-red-300 animate-spin" />
                        Deleting…
                      </>
                    ) : (
                      "🗑 Delete"
                    )}
                  </button>
                </Tooltip>
              </div>
            </GlassPanel>
          ))}
        </div>
      )}

      {/* Create List Modal */}
      <Modal
        open={isCreateListDialogOpen}
        onClose={() => {
          setIsCreateListDialogOpen(false);
          listNameInput.setValue("");
        }}
        panelClassName="w-full max-w-md rounded-3xl border border-white/10 bg-surface-card/95 backdrop-blur-xl shadow-card"
      >
        <div className="px-6 pt-6 pb-2">
          <h3 className="font-heading font-bold text-xl text-white">Create new list</h3>
          <p className="text-white/50 text-sm mt-1">Give your collection a name.</p>
        </div>
        <div className="px-6 py-4">
          <TextInput
            label="List Name"
            autoFocus
            value={listNameInput.value}
            onChange={listNameInput.handleChange}
            ref={listNameInput.inputRef}
            placeholder="e.g., Want to Play, Completed, Favorites..."
          />
        </div>
        <div className="flex justify-end gap-3 px-6 pb-6">
          <Button
            variant="ghost"
            onClick={() => {
              setIsCreateListDialogOpen(false);
              listNameInput.setValue("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!listNameInput.value.trim()}
            onClick={handleCreateList}
          >
            Create list
          </Button>
        </div>
      </Modal>

      {/* Game Selection Modal */}
      <Modal
        open={isGameSelectionDialogOpen}
        onClose={() => {
          setIsGameSelectionDialogOpen(false);
          setSelectedListForGames(null);
          gameSearchInput.setValue("");
        }}
        panelClassName="w-full max-w-3xl rounded-3xl border border-white/10 bg-surface-card/95 backdrop-blur-xl shadow-card min-h-[60vh]"
      >
        <div className="px-6 pt-6 pb-2">
          <h3 className="font-heading font-bold text-xl text-white">
            Add games to “{selectedListForGames?.name}”
          </h3>
        </div>
        <div className="px-6 py-4">
          <TextInput
            icon="🔍"
            placeholder="Search games..."
            value={gameSearchInput.value}
            onChange={(e) => {
              gameSearchInput.handleChange(e);
              if (e.target.value.length > 2) {
                loadAvailableGames();
              }
            }}
            ref={gameSearchInput.inputRef}
            className="mb-4"
          />

          {searchingGames ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 rounded-full border-4 border-brand-purple/30 border-t-brand-purple animate-spin" />
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-3 pr-1">
              {availableGames.map((game) => {
                const isGameInList = selectedListForGames?.games?.some(
                  (g) => g.id === game.id,
                );
                return (
                  <div
                    key={game.id}
                    className={[
                      "rounded-xl p-3 flex items-center gap-3 border transition-colors",
                      isGameInList
                        ? "bg-neon-green/10 border-neon-green/40"
                        : "bg-white/[0.03] border-white/10 hover:border-brand-purple/40",
                    ].join(" ")}
                  >
                    <img
                      src={game.cover_url || game.image}
                      alt={game.title || game.name}
                      className="w-[46px] h-[64px] object-cover rounded-lg shrink-0"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/50x70?text=No+Image";
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={[
                          "font-bold truncate",
                          isGameInList ? "text-neon-green" : "text-white",
                        ].join(" ")}
                      >
                        {game.title || game.name}
                      </p>
                      <p className="text-white/50 text-xs">
                        {game.release_date &&
                          `${new Date(game.release_date).getFullYear()}`}
                      </p>
                      {isGameInList && (
                        <span className="inline-block mt-1 rounded-full bg-neon-green/20 text-neon-green text-[11px] px-2 py-0.5">
                          Already added
                        </span>
                      )}
                    </div>
                    <Tooltip label={isGameInList ? "Already in this list" : "Add to list"}>
                      <button
                        type="button"
                        onClick={() => handleAddGameToList(game)}
                        disabled={isGameInList}
                        aria-label="Add game to list"
                        className={[
                          "h-9 w-9 rounded-full flex items-center justify-center text-lg transition-colors",
                          isGameInList
                            ? "text-neon-green cursor-not-allowed"
                            : "text-brand-purple-light hover:bg-brand-purple/15",
                        ].join(" ")}
                      >
                        {isGameInList ? "✓" : "+"}
                      </button>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-end px-6 pb-6">
          <Button
            variant="primary"
            onClick={() => {
              setIsGameSelectionDialogOpen(false);
              setSelectedListForGames(null);
              gameSearchInput.setValue("");
            }}
          >
            Done
          </Button>
        </div>
      </Modal>

      {/* Smart List Generator Modal */}
      <Modal
        open={isSmartListDialogOpen}
        onClose={() => setIsSmartListDialogOpen(false)}
        panelClassName="w-full max-w-3xl rounded-3xl border border-white/10 bg-surface-card/95 backdrop-blur-xl shadow-card"
      >
        <div className="px-6 pt-6 pb-2">
          <h3 className="font-heading font-bold text-xl text-white">
            ✨ Auto-generate a smart list
          </h3>
          <p className="text-white/55 text-sm mt-1">
            Pick a few preferences and we'll curate a list for you.
          </p>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-4">
              <TextInput
                label="List Name"
                value={smartListCriteria.listName}
                onChange={(e) =>
                  setSmartListCriteria({
                    ...smartListCriteria,
                    listName: e.target.value,
                  })
                }
                placeholder="e.g., Best Roguelikes for PS4"
              />

              <TagMultiSelect
                label="Genres"
                options={genreOptions}
                value={smartListCriteria.genres}
                onChange={(newValue) =>
                  setSmartListCriteria({
                    ...smartListCriteria,
                    genres: newValue,
                  })
                }
                placeholder="Select genres..."
              />

              <TagMultiSelect
                label="Platforms"
                options={platformOptions}
                value={smartListCriteria.platforms}
                onChange={(newValue) =>
                  setSmartListCriteria({
                    ...smartListCriteria,
                    platforms: newValue,
                  })
                }
                placeholder="Select platforms..."
              />
            </div>

            <div className="space-y-4">
              <TagMultiSelect
                label="Tags/Themes"
                options={tagOptions}
                value={smartListCriteria.tags}
                onChange={(newValue) =>
                  setSmartListCriteria({
                    ...smartListCriteria,
                    tags: newValue,
                  })
                }
                placeholder="Select tags..."
              />

              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-white/80 mb-2 text-sm font-semibold">
                  Minimum rating: {smartListCriteria.rating}/10
                </p>
                <input
                  type="range"
                  min={3}
                  max={10}
                  step={0.5}
                  value={smartListCriteria.rating}
                  onChange={(e) =>
                    setSmartListCriteria({
                      ...smartListCriteria,
                      rating: Number(e.target.value),
                    })
                  }
                  className="w-full accent-brand-purple"
                />
              </div>
            </div>

            <div className="md:col-span-2 rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center justify-between gap-4">
              <p className="text-white/80 text-sm font-semibold whitespace-nowrap">
                Number of games: {smartListCriteria.limit}
              </p>
              <input
                type="range"
                min={5}
                max={20}
                step={1}
                value={smartListCriteria.limit}
                onChange={(e) =>
                  setSmartListCriteria({
                    ...smartListCriteria,
                    limit: Number(e.target.value),
                  })
                }
                className="w-48 accent-brand-purple"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 pb-6 pt-2 border-t border-white/10">
          <Button variant="ghost" onClick={() => setIsSmartListDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={generatingSmartList}
            onClick={handleGenerateSmartList}
          >
            {generatingSmartList ? "Generating..." : "✨ Generate list"}
          </Button>
        </div>
      </Modal>

      {/* Generated List Preview Modal */}
      <Modal
        open={showGeneratedListDialog}
        onClose={() => setShowGeneratedListDialog(false)}
        panelClassName="w-full max-w-4xl rounded-3xl border border-white/10 bg-surface-card/95 backdrop-blur-xl shadow-card"
      >
        {generatedList && (
          <>
            <div className="px-6 pt-6 pb-2">
              <h3 className="font-heading font-bold text-xl text-white">
                ✨ {generatedList.name}
              </h3>
              {generatedList.description && (
                <p className="text-white/55 text-sm mt-1">{generatedList.description}</p>
              )}
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {generatedList.games.slice(0, 8).map((game) => (
                  <div
                    key={game.id}
                    className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center"
                  >
                    <p className="text-white text-sm font-bold mb-1 truncate">
                      {game.title || game.name}
                    </p>
                    <p className="text-white/50 text-xs truncate">
                      {game.genres
                        ?.slice(0, 2)
                        .map((g) => (typeof g === "object" ? g.name : g))
                        .join(", ")}
                    </p>
                  </div>
                ))}
              </div>

              {generatedList.games.length > 8 && (
                <p className="text-white/50 text-center mt-4 text-sm">
                  … and {generatedList.games.length - 8} more games
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <Button
                variant="ghost"
                disabled={savingGeneratedList}
                onClick={() => setShowGeneratedListDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={savingGeneratedList}
                onClick={handleSaveGeneratedList}
              >
                {savingGeneratedList ? (
                  <>
                    <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    Saving…
                  </>
                ) : (
                  "+ Save to my lists"
                )}
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* View List Modal — open a saved list to see all its games */}
      <Modal
        open={isViewListDialogOpen}
        onClose={() => {
          setIsViewListDialogOpen(false);
          setViewList(null);
        }}
        panelClassName="w-full max-w-4xl rounded-3xl border border-white/10 bg-surface-card/95 backdrop-blur-xl shadow-card"
      >
        {viewList && (
          <>
            <div className="px-6 pt-6 pb-2 flex items-start gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-xl bg-brand-purple/15 border border-brand-purple/30 text-xl shrink-0">
                📋
              </span>
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-xl text-white truncate">
                  {viewList.name}
                </h3>
                <p className="text-white/50 text-sm mt-0.5">
                  {viewList.games?.length || 0} games
                </p>
              </div>
            </div>
            <div className="px-6 py-4">
              {!viewList.games || viewList.games.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center">
                  <div className="text-5xl mb-3 opacity-70">🎮</div>
                  <p className="text-white/60 mb-4">This list has no games yet.</p>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setIsViewListDialogOpen(false);
                      handleOpenGameSelection(viewList);
                    }}
                  >
                    + Add games
                  </Button>
                </div>
              ) : (
                <div className="max-h-[55vh] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pr-1">
                  {viewList.games.map((game) => (
                    <div
                      key={game.id}
                      className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex flex-col items-center text-center"
                    >
                      <img
                        src={game.cover_url || game.image}
                        alt={game.title || game.name}
                        className="w-full aspect-[3/4] object-cover rounded-lg mb-2"
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/120x160?text=No+Image";
                        }}
                      />
                      <p className="text-white text-sm font-bold truncate w-full">
                        {game.title || game.name}
                      </p>
                      {game.release_date && (
                        <p className="text-white/45 text-xs">
                          {new Date(game.release_date).getFullYear()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-between gap-3 px-6 pb-6 pt-2 border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => {
                  setIsViewListDialogOpen(false);
                  handleOpenGameSelection(viewList);
                }}
              >
                + Add games
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setIsViewListDialogOpen(false);
                  setViewList(null);
                }}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );

  const renderStatisticsTab = () => (
    <div>
      {sectionHeading("Your gaming stats")}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-6">
        <StatCard
          icon="❤️"
          value={userStats.totalFavorites}
          label="Favorite games"
          accent="border-red-400/30 bg-gradient-to-br from-red-400/12 to-transparent hover:border-red-400/60"
          delay={0}
        />
        <StatCard
          icon="📋"
          value={userStats.totalLists}
          label="Custom lists"
          accent="border-brand-purple/30 bg-gradient-to-br from-brand-purple/12 to-transparent hover:border-brand-purple/60"
          delay={60}
        />
        <StatCard
          icon="⭐"
          value={userStats.averageRating}
          label="Avg rating"
          accent="border-brand-gold/30 bg-gradient-to-br from-brand-gold/12 to-transparent hover:border-brand-gold/60"
          delay={120}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <GlassPanel className="p-6 animate-fadeUp">
          <h3 className="flex items-center gap-1.5 mb-5 font-heading font-bold text-lg text-white">
            Gaming preferences
            <InfoTooltip text="Calculated from the platforms and genres of the games you've favorited, the most frequent one across your favorites list." />
          </h3>

          <div className="mb-4">
            <p className="text-white/55 text-sm mb-1.5">Favourite console</p>
            <div className="rounded-xl border border-brand-gold/25 bg-brand-gold/10 p-3.5">
              <p className="text-brand-gold font-heading font-bold text-lg">
                {getMostCommonPlatform()}
              </p>
              <p className="text-white/55 text-sm">
                {favoriteGames.length > 0
                  ? `${Math.round((getMostCommonPlatformCount() / favoriteGames.length) * 100)}% of favourites`
                  : "45% of favourites"}
              </p>
            </div>
          </div>

          <div>
            <p className="text-white/55 text-sm mb-1.5">Favourite genre</p>
            <div className="rounded-xl border border-brand-purple/25 bg-brand-purple/10 p-3.5">
              <p className="text-brand-purple-light font-heading font-bold text-lg">
                {getMostCommonGenre()}
              </p>
              <p className="text-white/55 text-sm">
                {favoriteGames.length > 0
                  ? `${Math.round((getMostCommonGenreCount() / favoriteGames.length) * 100)}% of favourites`
                  : "35% of favourites"}
              </p>
            </div>
          </div>
        </GlassPanel>

        <GlassPanel className="p-6 animate-fadeUp">
          <h3 className="flex items-center gap-1.5 mb-5 font-heading font-bold text-lg text-white">
            Top tags
            <InfoTooltip text="The tags/themes that appear most often across your favorited games, ranked by how large a share of your favorites they cover." />
          </h3>

          <div>
            {getTopTags().map((tag, index) => (
              <div key={tag.name} className="mb-3.5">
                <div className="flex justify-between mb-1.5">
                  <span className="text-white/85 font-semibold text-sm">{tag.name}</span>
                  <span className="text-white/50 text-sm">{tag.percentage}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: `${tag.percentage}%`,
                      background:
                        index === 0
                          ? "linear-gradient(90deg,#ffd84d,#efbf04)"
                          : index === 1
                            ? "linear-gradient(90deg,rgb(178,84,255),rgb(94,1,212))"
                            : "linear-gradient(90deg,#a7c957,#7cb342)",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </GlassPanel>
      </div>

      <GlassPanel className="p-6 animate-fadeUp">
        <h3 className="mb-5 font-heading font-bold text-lg text-white">Gaming activity</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Lists created", value: customLists.length },
            {
              label: "Games in lists",
              value: customLists.reduce(
                (total, list) => total + (list.games?.length || 0),
                0,
              ),
            },
            {
              label: "Generated lists",
              value: customLists.filter((list) => list.isGenerated).length,
            },
            { label: "Unique games", value: getUniqueGamesCount() },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-center"
            >
              <p className="text-white font-heading font-extrabold text-3xl">{item.value}</p>
              <p className="text-white/55 text-sm mt-1">{item.label}</p>
            </div>
          ))}
        </div>
      </GlassPanel>
    </div>
  );

  const renderForYouTab = () => (
    <div>
      {sectionHeading("Personalized for you")}

      <GlassPanel className="p-6 md:p-8 animate-fadeUp">
        <div className="flex items-center gap-3 mb-6">
          <span className="flex items-center justify-center h-12 w-12 rounded-2xl bg-brand-purple/15 border border-brand-purple/30 text-2xl">
            ✨
          </span>
          <div>
            <h3 className="flex items-center gap-1.5 font-heading font-bold text-lg text-white">
              AI-powered recommendations
              <InfoTooltip text="We take one of your favorite games as a seed and ask the recommendation engine for similar titles. If it can't find enough, we fill remaining slots from generally similar or popular games so you always see 4 suggestions." />
            </h3>
            <p className="text-white/55 text-sm mt-0.5">
              Based on your favourite games and preferences.
            </p>
          </div>
        </div>

        {recLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="h-14 w-14 rounded-full border-4 border-brand-purple/30 border-t-brand-purple animate-spin mb-4" />
            <p className="font-heading font-bold text-white mb-1">
              Finding perfect games for you…
            </p>
            <p className="text-white/55 text-sm">
              Analysing your favourites to suggest similar titles.
            </p>
          </div>
        ) : !favoriteGames || favoriteGames.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-12 text-center">
            <div className="text-6xl mb-4 opacity-70">❤️</div>
            <h4 className="mb-2 font-heading font-bold text-xl text-white">
              No favourites yet
            </h4>
            <p className="mb-5 text-white/60 leading-relaxed max-w-lg mx-auto">
              Add games to your favourites to unlock personalised recommendations tuned to
              your taste.
            </p>
            <Button variant="outline">❤️ Browse games to favourite</Button>
          </div>
        ) : personalizedRecommendations.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-12 text-center">
            <div className="text-6xl mb-4 opacity-80">✨</div>
            <h4 className="mb-2 font-heading font-bold text-xl text-white">
              Recommendations loading…
            </h4>
            <p className="mb-5 text-white/60 leading-relaxed max-w-lg mx-auto">
              We're analysing your favourite games to find perfect matches. This may take a
              moment.
            </p>
            <Tooltip label="Reload the page to fetch fresh recommendations" wide>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Refresh recommendations
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div>
            <h4 className="flex items-center gap-1.5 mb-5 font-heading font-bold text-white">
              <span>⭐</span>
              Recommended just for you ({personalizedRecommendations.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              {(() => {
                const slots = [...personalizedRecommendations];
                while (slots.length < 4) slots.push(null);
                return slots.slice(0, 4).map((game, idx) => (
                  <div key={game ? game.id : `placeholder-${idx}`}>
                    {game ? (
                      <GameCard game={game} />
                    ) : (
                      <div className="h-[500px] rounded-3xl border border-dashed border-white/12 bg-white/[0.02] flex items-center justify-center">
                        <p className="px-4 text-center text-white/50 text-sm">
                          More recommendations coming soon
                        </p>
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>

            {personalizedRecommendations.length > 0 && (
              <div className="text-center mt-8">
                <div className="border-t border-white/10 mb-4" />
                <p className="text-white/45 text-sm">
                  Based on games similar to your favourites:{" "}
                  {favoriteGames
                    .slice(0, 3)
                    .map((game) => game.title || game.name)
                    .join(", ")}
                  {favoriteGames.length > 3 && ` and ${favoriteGames.length - 3} more`}
                </p>
              </div>
            )}
          </div>
        )}
      </GlassPanel>
    </div>
  );

  const renderBlacklistTab = () => (
    <div>
      {sectionHeading("Blacklisted games")}

      {blacklistgames.length === 0 ? (
        <GlassPanel className="p-12 text-center animate-fadeUp">
          <div className="text-6xl mb-4 opacity-70">🚫</div>
          <p className="text-white/60">You haven't blacklisted any games yet.</p>
        </GlassPanel>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {blacklistgames.map((game, index) => (
            <div key={`rec-${game.id}-${index}`} className="h-full">
              <GameCard
                game={game}
                showActions={true}
                handleRefresh={blacklistRefresh}
                source="profile"
                recommendedFrom="personalized"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 0:
        return renderProfileTab();
      case 1:
        return renderListsTab();
      case 2:
        return renderStatisticsTab();
      case 3:
        return renderForYouTab();
      case 4:
        return renderBlacklistTab();
      default:
        return renderProfileTab();
    }
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  const toastColors = {
    success: "border-green-500/50 bg-green-500/15 text-green-300",
    error: "border-red-500/50 bg-red-500/15 text-red-300",
    warning: "border-brand-gold/50 bg-brand-gold/15 text-brand-gold",
  };

  const quickStats = [
    { label: "Favourites", value: userStats.totalFavorites, icon: "❤️", color: "#FF6B6B" },
    { label: "Lists", value: userStats.totalLists, icon: "📋", color: "#9701fb" },
    { label: "Blacklisted", value: blacklistgames.length, icon: "🚫", color: "#efbf04" },
  ];

  return (
    <div className="min-h-[calc(100vh-var(--header-height))] py-8 sm:py-10">
      {message.text && (
        <div
          className={[
            "fixed top-24 right-4 z-[200] max-w-sm rounded-xl border px-4 py-3 shadow-card backdrop-blur-md animate-fadeIn",
            toastColors[message.type] || toastColors.warning,
          ].join(" ")}
        >
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm">{message.text}</p>
            <button
              type="button"
              onClick={() => setMessage({ type: "", text: "" })}
              className="text-current opacity-70 hover:opacity-100"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-[1200px] px-3 sm:px-4 md:px-6">
        {/* Dashboard header */}
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-surface-card/40 backdrop-blur-md mb-6 p-6 sm:p-8">
          <div className="absolute inset-0 gg-grid-backdrop opacity-40" />
          <div className="absolute -top-24 left-10 h-56 w-72 rounded-full bg-brand-purple/25 blur-3xl animate-pulseGlow" />
          <div className="absolute top-4 right-8 h-28 w-28 rounded-full bg-brand-gold/15 blur-2xl animate-floaty" />

          <div className="relative flex flex-col sm:flex-row items-center sm:items-end gap-5 sm:gap-7">
            <div className="relative shrink-0">
              <div className="absolute -inset-1.5 rounded-full bg-brand-gradient opacity-50 blur-lg" />
              <div className="relative h-24 w-24 sm:h-28 sm:w-28 rounded-full bg-brand-gradient flex items-center justify-center font-heading font-extrabold text-4xl text-white border-2 border-white/15 shadow-glow-purple">
                {user?.username?.charAt(0)?.toUpperCase() || "U"}
              </div>
            </div>

            <div className="flex-1 text-center sm:text-left min-w-0">
              <p className="text-[0.7rem] font-bold uppercase tracking-[0.25em] text-brand-gold/80 mb-1">
                Player dashboard
              </p>
              <h1 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl leading-tight truncate">
                <span className="gg-gradient-text">{user?.username || "User"}</span>
              </h1>
              <p className="text-white/55 text-sm mt-1 truncate">{user?.email}</p>
            </div>

            <div className="flex gap-2.5 sm:gap-3 w-full sm:w-auto">
              {quickStats.map((s) => (
                <div
                  key={s.label}
                  className="flex-1 sm:flex-none sm:min-w-[154px] rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 backdrop-blur-sm flex items-center gap-3"
                >
                  <span
                    className="flex items-center justify-center h-10 w-10 rounded-xl text-lg shrink-0 border"
                    style={{ borderColor: `${s.color}55`, background: `${s.color}1f` }}
                  >
                    {s.icon}
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading font-extrabold text-2xl text-white leading-none">
                      {s.value}
                    </p>
                    <p className="text-white/50 text-[0.66rem] font-semibold uppercase tracking-wide mt-1">
                      {s.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Tabs — dashboard tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-6">
          {TABS.map((tab, idx) => {
            const active = activeTab === idx;
            return (
              <Tooltip key={tab.label} label={tab.desc} side="bottom" wide className="w-full">
                <button
                  type="button"
                  onClick={() => handleTabChange(idx)}
                  aria-label={`${tab.label}: ${tab.desc}`}
                  className={[
                    "group w-full flex items-center gap-3 rounded-2xl border px-3.5 py-3 text-left transition-all duration-200",
                    active
                      ? "border-transparent bg-brand-gradient shadow-glow-purple -translate-y-0.5"
                      : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.07] hover:-translate-y-0.5",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "flex items-center justify-center h-10 w-10 rounded-xl text-xl shrink-0 transition-colors",
                      active ? "bg-white/20" : "bg-white/[0.06]",
                    ].join(" ")}
                  >
                    {tab.icon}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={[
                        "block font-heading font-bold text-sm leading-tight truncate",
                        active ? "text-white" : "text-white/85",
                      ].join(" ")}
                    >
                      {tab.label}
                    </span>
                    <span
                      className={[
                        "hidden lg:block text-[0.68rem] leading-tight truncate mt-0.5",
                        active ? "text-white/75" : "text-white/40",
                      ].join(" ")}
                    >
                      {tab.desc}
                    </span>
                  </span>
                </button>
              </Tooltip>
            );
          })}
        </div>

        {/* Tab Content — keyed so switching tabs animates in, but state
            changes within a tab never remount it (no focus loss / jerk). */}
        <div key={activeTab} className="animate-fadeUp">
          {renderActiveTab()}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
